import React, { useEffect, useMemo, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function FacePortrait({ face, size }) {
  const imagePath = face?.appearance?.imagePath || face?.imagePath || null;

  if (!imagePath) {
    return <div className="rounded-2xl bg-slate-200 dark:bg-slate-700" style={{ width: size, height: size }} aria-hidden="true" />;
  }

  return (
    <div
      className="flex items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700/90"
      style={{ width: size, height: size }}
    >
      <img
        src={imagePath}
        alt={face?.name || 'Face portrait'}
        width={size}
        height={size}
        className="block object-contain"
        style={{ width: size, height: size }}
        loading="lazy"
        draggable="false"
      />
    </div>
  );
}

function PlayerPill({ label, value, active, winner }) {
  return (
    <div className={`rounded-2xl border px-3 py-2 ${winner ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-950/40' : active ? 'border-cyan-400 bg-cyan-50 dark:border-cyan-500 dark:bg-cyan-950/40' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'}`}>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function formatQuestionAnnouncement(prompt) {
  const trimmedPrompt = String(prompt || '').trim().replace(/\?+$/, '');
  if (!trimmedPrompt) return '';

  if (/^are they\s+/i.test(trimmedPrompt)) {
    return `if they are ${trimmedPrompt.replace(/^are they\s+/i, '')}`;
  }

  if (/^do they\s+/i.test(trimmedPrompt)) {
    return `if they ${trimmedPrompt.replace(/^do they\s+/i, '')}`;
  }

  return trimmedPrompt.charAt(0).toLowerCase() + trimmedPrompt.slice(1);
}

const EMPTY_ARRAY = [];

export default function FaceFinderGame({ gameState, gamePlayers = [], user = {}, socket, roomId }) {
  const { submitAction, isLocalPlayerTurn, findPlayerById } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [selectedGuessFaceId, setSelectedGuessFaceId] = useState(null);
  const [questionPath, setQuestionPath] = useState([]);
  const [isQuestionToastVisible, setIsQuestionToastVisible] = useState(false);
  const [questionAnnouncementStage, setQuestionAnnouncementStage] = useState('question');
  const [guessAnnouncementState, setGuessAnnouncementState] = useState({ visible: false, stage: 'suspense', key: null });
  const [isGuessProgressActive, setIsGuessProgressActive] = useState(false);

  const players = gameState?.players || EMPTY_ARRAY;
  const faces = gameState?.faces || EMPTY_ARRAY;
  const phase = gameState?.phase || 'playing';
  const myId = user?.socketId;
  const currentPlayerId = gameState?.currentPlayer || null;
  const myTurn = isLocalPlayerTurn();
  const eliminated = new Set(gameState?.eliminatedByPlayer?.[myId] || []);
  const questionTree = gameState?.questionTree || EMPTY_ARRAY;

  useEffect(() => {
    if (phase !== 'playing') {
      setSelectedGuessFaceId(null);
      setQuestionPath([]);
    }
  }, [phase]);

  useEffect(() => {
    if (!myTurn) {
      setSelectedGuessFaceId(null);
    }
  }, [myTurn]);

  const currentPlayer = findPlayerById(currentPlayerId);
  const matchWinner = gameState?.winners?.length ? findPlayerById(gameState.winners[0]) : null;
  const revealedFace = useMemo(() => faces.find((face) => face.id === gameState?.revealedFaceId) || null, [faces, gameState?.revealedFaceId]);
  const questionTrail = useMemo(() => {
    const trail = [];
    let currentLevel = questionTree;

    questionPath.forEach((key) => {
      const node = currentLevel.find((item) => item.key === key);
      if (!node) return;
      trail.push(node);
      currentLevel = Array.isArray(node.children) ? node.children : EMPTY_ARRAY;
    });

    return trail;
  }, [questionPath, questionTree]);

  const activeQuestionNode = questionTrail[questionTrail.length - 1] || null;
  const visibleQuestionItems = activeQuestionNode?.children || questionTree;
  const questionPanelTitle = activeQuestionNode?.label || 'Topics';

  const statusText = useMemo(() => {
    if (phase === 'finished') {
      return `${matchWinner?.username || 'A player'} found the hidden face.`;
    }
    if (myTurn) {
      return 'Your turn: ask one yes/no question or make one guess.';
    }
    return `${currentPlayer?.username || 'Another player'} is taking their turn.`;
  }, [currentPlayer?.username, matchWinner?.username, myTurn, phase]);

  const askQuestion = async (questionKey) => {
    if (!myTurn) return;
    await submitAction({ type: 'askQuestion', questionKey });
    setQuestionPath([]);
    setSelectedGuessFaceId(null);
  };

  const toggleElimination = async (faceId) => {
    await submitAction({ type: 'toggleElimination', faceId });
  };

  const makeGuess = async () => {
    if (!myTurn || !selectedGuessFaceId) return;
    await submitAction({ type: 'makeGuess', faceId: selectedGuessFaceId });
  };

  const openQuestionNode = (node, depth) => {
    setQuestionPath((currentPath) => {
      const nextPath = currentPath.slice(0, depth);
      nextPath.push(node.key);
      return nextPath;
    });
  };

  const lastQuestion = gameState?.lastQuestion;
  const history = gameState?.questionHistory || [];
  const latestHistoryEntry = history.length > 0 ? history[history.length - 1] : null;
  const lastGuess = gameState?.lastGuess || null;
  const lastGuessedFace = useMemo(() => faces.find((face) => face.id === lastGuess?.faceId) || null, [faces, lastGuess?.faceId]);
  const lastGuessingPlayer = lastGuess?.by ? findPlayerById(lastGuess.by) : null;
  const latestGuessSignature = latestHistoryEntry?.type === 'guess' && lastGuess?.faceId
    ? `${history.length}:${lastGuess.by}:${lastGuess.faceId}:${lastGuess.correct ? 'correct' : 'wrong'}`
    : null;
  const latestQuestionSignature = lastQuestion?.type === 'question'
    ? `${lastQuestion.turn}:${lastQuestion.by}:${lastQuestion.questionKey}:${lastQuestion.answer ? 'yes' : 'no'}`
    : null;
  const questionAnnouncementText = useMemo(() => {
    if (!lastQuestion?.prompt) return null;
    const askingPlayer = findPlayerById(lastQuestion.by)?.username || 'A player';
    return `${askingPlayer} asked ${formatQuestionAnnouncement(lastQuestion.prompt)}.`;
  }, [findPlayerById, lastQuestion]);

  useEffect(() => {
    if (!latestQuestionSignature) return undefined;

    setIsQuestionToastVisible(true);
    setQuestionAnnouncementStage('question');

    const revealTimeoutId = window.setTimeout(() => {
      setQuestionAnnouncementStage('answer');
    }, 2200);

    const hideTimeoutId = window.setTimeout(() => {
      setIsQuestionToastVisible(false);
    }, 5200);

    return () => {
      window.clearTimeout(revealTimeoutId);
      window.clearTimeout(hideTimeoutId);
    };
  }, [latestQuestionSignature]);

  useEffect(() => {
    if (!latestGuessSignature) return undefined;

    setGuessAnnouncementState({ visible: true, stage: 'suspense', key: latestGuessSignature });
    setIsGuessProgressActive(false);

    const animationFrameId = window.requestAnimationFrame(() => {
      setIsGuessProgressActive(true);
    });

    const revealTimeoutId = window.setTimeout(() => {
      setGuessAnnouncementState((currentState) => {
        if (currentState.key !== latestGuessSignature) return currentState;
        return { ...currentState, stage: 'result' };
      });
    }, 1800);

    const hideTimeoutId = window.setTimeout(() => {
      setGuessAnnouncementState((currentState) => {
        if (currentState.key !== latestGuessSignature) return currentState;
        return { ...currentState, visible: false };
      });
    }, lastGuess?.correct ? 2400 : 3200);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(revealTimeoutId);
      window.clearTimeout(hideTimeoutId);
    };
  }, [lastGuess?.correct, latestGuessSignature]);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-3 text-slate-900 dark:text-slate-100">
      {guessAnnouncementState.visible && lastGuess && lastGuessedFace ? (
        <div
          key={guessAnnouncementState.key || latestGuessSignature || `${lastGuess.by}:${lastGuess.faceId}`}
          className="pointer-events-none fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-[3px]"
        >
          <div className="w-full max-w-md rounded-[2rem] border border-rose-300 bg-white/95 p-5 text-center shadow-[0_28px_90px_rgba(15,23,42,0.38)] ring-1 ring-white/60 dark:border-rose-700 dark:bg-slate-900/95 dark:ring-slate-700/80">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-rose-700 dark:text-rose-300">Guess Locked In</div>
            <div className="mt-3 flex justify-center">
              <FacePortrait face={lastGuessedFace} size={160} />
            </div>
            <div className="mt-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {(lastGuessingPlayer?.username || 'A player')} guessed {lastGuessedFace.name}
            </div>
            <div className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              {guessAnnouncementState.stage === 'suspense'
                ? 'Checking the guess...'
                : lastGuess.correct
                  ? 'That guess is correct.'
                  : 'That guess is wrong.'}
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div
                className={`h-full rounded-full transition-all duration-[1800ms] ease-out ${guessAnnouncementState.stage === 'result' ? lastGuess.correct ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-rose-500 dark:bg-rose-400' : 'bg-amber-400 dark:bg-amber-300'} ${isGuessProgressActive || guessAnnouncementState.stage === 'result' ? 'w-full' : 'w-0'}`}
              />
            </div>
          </div>
        </div>
      ) : null}

      {isQuestionToastVisible && questionAnnouncementText ? (
        <div className="pointer-events-none fixed inset-0 z-[210] flex items-center justify-center bg-slate-950/30 px-4 backdrop-blur-[2px]">
          <div className="pointer-events-auto w-full max-w-xl rounded-3xl border border-cyan-300 bg-white/90 p-5 text-center shadow-[0_24px_80px_rgba(15,23,42,0.35)] ring-1 ring-white/60 dark:border-cyan-700 dark:bg-slate-900/90 dark:ring-slate-700/80">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700 dark:text-cyan-300">Question Asked</div>
            <div className="mt-3 text-base font-semibold leading-relaxed text-slate-900 dark:text-slate-100 sm:text-lg">{questionAnnouncementText}</div>
            {questionAnnouncementStage === 'question' ? (
              <div className="mt-3 text-sm text-slate-500 dark:text-slate-300">Thinking...</div>
            ) : (
              <div className="mt-4 flex flex-col items-center gap-2">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full text-2xl ${lastQuestion?.answer ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300'}`}>
                  {lastQuestion?.answer ? '👍' : '👎'}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-300">
                  {`The answer is ${lastQuestion?.answer ? 'Yes' : 'No'}.`}
                </div>
              </div>
            )}
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div
                className={`h-full rounded-full transition-all duration-[2200ms] ease-out ${questionAnnouncementStage === 'answer' ? lastQuestion?.answer ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-rose-500 dark:bg-rose-400' : 'bg-cyan-500 dark:bg-cyan-400'} ${questionAnnouncementStage === 'answer' ? 'w-full' : 'w-0'}`}
              />
            </div>
            {questionAnnouncementStage === 'answer' ? (
              <div className="mt-4 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setIsQuestionToastVisible(false)}
                  className="rounded-full bg-cyan-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-400"
                >
                  OK
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-cyan-950/30">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-slate-600 dark:text-slate-300">{statusText}</p>
          </div>
          {phase === 'finished' && revealedFace ? (
            <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-white/80 px-3 py-2 shadow-sm dark:border-emerald-800 dark:bg-slate-900/80">
              <FacePortrait face={revealedFace} size={72} />
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Found Face</div>
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{revealedFace.name}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              {players.map((player) => (
                <PlayerPill
                  key={player.socketId}
                  label={player.username || player.socketId}
                  value={matchWinner?.socketId === player.socketId ? 'Winner' : currentPlayerId === player.socketId && phase === 'playing' ? 'Active turn' : 'Waiting'}
                  active={player.socketId === currentPlayerId && phase === 'playing'}
                  winner={matchWinner?.socketId === player.socketId}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]"> */}
        <div className="flex flex-col gap-4">
          {phase === 'playing' && myTurn ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-600 dark:bg-slate-900">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Question Picker</h3>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${myTurn ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                  {myTurn ? 'Your turn' : 'Waiting'}
                </span>
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setQuestionPath([])}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-cyan-300 hover:text-cyan-700 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-cyan-500 dark:hover:text-cyan-200"
                >
                  All topics
                </button>
                {questionTrail.map((node, index) => (
                  <button
                    key={node.key}
                    type="button"
                    onClick={() => setQuestionPath(questionPath.slice(0, index + 1))}
                    className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-cyan-300 hover:text-cyan-700 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-cyan-500 dark:hover:text-cyan-200"
                  >
                    {node.label}
                  </button>
                ))}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-500 dark:bg-slate-800">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-300">{questionPanelTitle}</div>
                  {questionPath.length > 0 ? (
                    <button
                      type="button"
                      disabled={!myTurn}
                      onClick={() => setQuestionPath((currentPath) => currentPath.slice(0, -1))}
                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-cyan-300 hover:text-cyan-700 disabled:cursor-default disabled:opacity-50 dark:border-slate-400 dark:bg-slate-700 dark:text-slate-100 dark:hover:border-cyan-500 dark:hover:text-cyan-200"
                    >
                      Back
                    </button>
                  ) : null}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {visibleQuestionItems.map((node) => {
                    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
                    return (
                      <button
                        key={node.key}
                        type="button"
                        disabled={!myTurn}
                        onClick={() => {
                          if (hasChildren) {
                            openQuestionNode(node, questionPath.length);
                            return;
                          }
                          if (node.questionKey) {
                            askQuestion(node.questionKey);
                          }
                        }}
                        className={`rounded-2xl border px-3 py-2 text-left text-sm ${myTurn ? 'border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50 dark:border-slate-400 dark:bg-slate-700 dark:text-slate-100 dark:hover:border-cyan-500 dark:hover:bg-slate-600' : 'border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}
                      >
                        <div className="flex items-center justify-between gap-3 font-medium">
                          <span>{node.label}</span>
                          {hasChildren ? <span className="text-base leading-none text-slate-400 dark:text-slate-200">&rsaquo;</span> : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">Face Board</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Tap the name bar to select a guess and tap the portrait to toggle the cross on your own board.
                </p>
              </div>
            </div>

            <div className="pb-2">
              <div className="relative grid grid-cols-3 gap-2 overflow-visible sm:gap-3">
                {faces.map((face) => {
                  const isEliminated = eliminated.has(face.id);
                  const isSelectedGuess = selectedGuessFaceId === face.id;
                  const isRevealedFace = gameState?.revealedFaceId === face.id;
                  return (
                    <div
                      key={face.id}
                      className={`group relative z-0 min-h-[8rem] overflow-visible rounded-[1.15rem] border transition sm:min-h-[9.25rem] sm:rounded-[1.4rem] ${isRevealedFace ? 'border-emerald-500 bg-emerald-50 shadow-[0_0_0_2px_rgba(16,185,129,0.16)] dark:bg-emerald-950/30' : isSelectedGuess ? 'z-[80] border-rose-500 bg-rose-50 shadow-[0_10px_24px_rgba(244,63,94,0.14)] dark:bg-rose-950/40' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950'}`}
                    >
                      <button
                        type="button"
                        title={`Guess ${face.name}`}
                        onClick={() => {
                          if (phase === 'playing' && myTurn) {
                            setSelectedGuessFaceId((currentFaceId) => (currentFaceId === face.id ? null : face.id));
                          }
                        }}
                        disabled={phase !== 'playing' || !myTurn}
                        className={`relative z-20 flex w-full items-center justify-center rounded-t-[1rem] border-b px-1.5 py-1 text-center text-[10px] font-semibold tracking-[0.02em] transition sm:rounded-t-[1.25rem] sm:text-[11px] ${isSelectedGuess ? 'border-rose-300 bg-rose-100 text-rose-700 dark:border-rose-700 dark:bg-rose-950/60 dark:text-rose-200' : 'border-slate-200 bg-white/80 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-400 dark:hover:bg-slate-800'} ${phase !== 'playing' || !myTurn ? 'cursor-default' : ''}`}
                      >
                        <div className="w-full px-1 leading-tight">
                          {face.name}
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleElimination(face.id)}
                        disabled={phase !== 'playing'}
                        className={`relative flex h-[calc(100%-2.75rem)] w-full items-center justify-center px-1 py-1.5 sm:px-1.5 sm:py-2 ${phase === 'playing' ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        <FacePortrait face={face} size={112} />

                        {isEliminated ? (
                          <div className="pointer-events-none absolute inset-x-[6%] inset-y-[4%] z-10 sm:inset-x-[5%] sm:inset-y-[3%]">
                            <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden="true">
                              <path
                                d="M12 10 C24 20, 36 32, 49 47 C63 64, 75 78, 88 90"
                                stroke="#dc2626"
                                strokeWidth="8"
                                strokeLinecap="round"
                                fill="none"
                                opacity="0.94"
                              />
                              <path
                                d="M88 10 C75 22, 63 34, 50 48 C37 64, 25 78, 12 90"
                                stroke="#dc2626"
                                strokeWidth="7.5"
                                strokeLinecap="round"
                                fill="none"
                                opacity="0.9"
                              />
                              <path
                                d="M12 11 C24 21, 36 33, 49 48 C63 65, 75 79, 88 91"
                                stroke="#7f1d1d"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                fill="none"
                                opacity="0.28"
                              />
                              <path
                                d="M88 11 C75 23, 63 35, 50 49 C37 65, 25 79, 12 91"
                                stroke="#7f1d1d"
                                strokeWidth="2.1"
                                strokeLinecap="round"
                                fill="none"
                                opacity="0.24"
                              />
                            </svg>
                          </div>
                        ) : null}
                      </button>

                      <div
                        className={`pointer-events-none absolute inset-x-2 top-full z-[90] pt-2 transition-all duration-200 ease-out ${isSelectedGuess && phase === 'playing' ? 'translate-y-0 opacity-100' : 'translate-y-[-6px] opacity-0'}`}
                      >
                        <button
                          type="button"
                          onClick={makeGuess}
                          disabled={!myTurn || !isSelectedGuess}
                          className={`pointer-events-auto w-full rounded-2xl px-3 py-2 text-sm font-semibold shadow-lg transition ${myTurn && isSelectedGuess ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500'}`}
                        >
                          Guess
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">

          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-lg font-semibold">Game Log</h3>
            {lastQuestion ? (
              <div className="mt-3 rounded-2xl bg-slate-50 p-3 text-sm dark:bg-slate-950">
                <div className="font-medium">Latest answer</div>
                <div className="mt-1 text-slate-600 dark:text-slate-300">{lastQuestion.prompt}</div>
                <div className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${isQuestionToastVisible && questionAnnouncementStage === 'question' ? 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300' : lastQuestion.answer ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'}`}>
                  {isQuestionToastVisible && questionAnnouncementStage === 'question' ? 'Revealing...' : lastQuestion.answer ? 'Yes' : 'No'}
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No questions answered yet.</p>
            )}

            {gameState?.lastGuess ? (
              <div className="mt-3 rounded-2xl border border-slate-200 p-3 text-sm dark:border-slate-700">
                <div className="font-medium">Last guess</div>
                <div className="mt-1 text-slate-600 dark:text-slate-300">
                  {(findPlayerById(gameState.lastGuess.by)?.username || 'A player')} guessed {gameState.lastGuess.faceName}
                </div>
                <div className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${gameState.lastGuess.correct ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'}`}>
                  {gameState.lastGuess.correct ? 'Correct guess' : 'Wrong guess'}
                </div>
              </div>
            ) : null}

            {history.length > 0 ? (
              <div className="mt-3 space-y-2">
                {history.slice().reverse().map((entry, index) => (
                  <div key={`${entry.questionKey || 'guess'}-${index}`} className="rounded-2xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
                    <div className="font-medium text-slate-700 dark:text-slate-200">{findPlayerById(entry.by)?.username || 'Player'}</div>
                    <div className="text-slate-500 dark:text-slate-400">{entry.prompt}</div>
                    <div className={`mt-1 text-xs font-semibold ${entry.answer ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{entry.answer ? 'Yes' : 'No'}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      {/* </div> */}
    </div>
  );
}

FaceFinderGame.Guide = function FaceFinderGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold text-slate-900 dark:text-slate-100">Face Finder</h3>
      <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-300">
        <li>The game chooses one hidden face for everyone to find.</li>
        <li>Each turn you may either ask one yes-or-no question or make one direct guess.</li>
        <li>Use the question picker to drill into categories such as gender, hats, hair, and age.</li>
        <li>Tap the Mark button on any card to eliminate it from your own board on mobile or desktop.</li>
        <li>The first correct guess wins immediately and reveals the target face for all players.</li>
      </ul>
    </div>
  );
};
